import {
  STUDY_OBJECTIVE_MAX_BULLETS,
  STUDY_OBJECTIVE_MAX_CHARS,
  type StudyGoals,
} from "@digital-twins/shared-types";

/**
 * The study objective as text, and the rules the builder row edits it under.
 *
 * Pure on purpose: no React, no workspace, no network. The row imports it to
 * validate before saving, and the tests import it to pin the parse. The server
 * keeps its own `normalizeStudyGoals` backstop for every non-UI path, so this
 * module is the friendly layer, never the only one.
 *
 * The whole objective is edited as ONE textarea (Fernando, 2026-08-09), so the
 * serialization is the contract between what the marketer types and what gets
 * stored: the first non-empty line is the decision, every other non-empty line
 * is a walk-away bullet.
 */

export { STUDY_OBJECTIVE_MAX_BULLETS, STUDY_OBJECTIVE_MAX_CHARS };

/** The prefix bullets are written with, and the one stripped back off on parse. */
const BULLET_PREFIX = "- ";

/**
 * Zero-width / invisible codepoints used to smuggle instructions past a reader
 * (U+200B ZW space, U+200C ZW non-joiner, U+200D ZW joiner, U+2060 word joiner,
 * U+FEFF BOM, U+00AD soft hyphen). Mirrors `OBJECTIVE_INVISIBLE_CHARS_RE` in
 * apps/backend/src/questionnaires/common/artifacts.builder.ts. No legitimate
 * objective needs one, and stripping them is invisible to the marketer, so it
 * is a silent fix rather than an error they could never act on.
 */
// Written as an alternation rather than a character class: a class holding
// ZWJ trips `no-misleading-character-class`, and the intent is one codepoint at
// a time anyway.
const INVISIBLE_CHARS_RE = /\u200B|\u200C|\u200D|\u2060|\uFEFF|\u00AD/g;

/**
 * C0/C1 control characters, newlines excluded here (the editor is line-based,
 * so line breaks are structure, not noise). Replaced with a space rather than
 * '' so two words never weld together; the whitespace collapse tidies up after.
 */
// eslint-disable-next-line no-control-regex -- matching control characters IS the point
const CONTROL_CHARS_RE = /[\u0000-\u0009\u000B-\u001F\u007F-\u009F]/g;


/**
 * The row shows every stored bullet, and its Save sends back what it shows,
 * so an unedited bullet is never recorded as removed (F13 review). Only blank
 * entries are dropped; the server stores none.
 */
export function cleanLearnBullets(learn: Array<string> | undefined): Array<string> {
  return (learn ?? [])
    .map((bullet) => (typeof bullet === "string" ? bullet.trim() : ""))
    .filter(Boolean);
}

/**
 * The silent fixes: strip invisible and control characters, collapse runs of
 * whitespace inside a line. Applied per line so the editor's structure (one
 * bullet per line) survives. The marketer cannot see what was removed, so an
 * error here would only baffle them.
 */
function sanitizeLine(line: string): string {
  return line
    .replace(INVISIBLE_CHARS_RE, "")
    .replace(CONTROL_CHARS_RE, " ")
    .replace(/[^\S\n]+/g, " ")
    .trim();
}

export interface StudyObjectiveDraft {
  decision: string;
  learn: Array<string>;
}

/**
 * Read block -> textarea. Decision on the first line, one "- " bullet per line
 * after it. Exactly the shape `parseStudyObjective` reads back, so entering and
 * leaving edit mode without typing is a no-op.
 */
export function serializeStudyObjective(
  goals: Pick<StudyGoals, "decision" | "learn"> | null | undefined,
): string {
  if (!goals) {
    return "";
  }
  const decision = (goals.decision ?? "").trim();
  const bullets = cleanLearnBullets(goals.learn).map(
    (bullet) => `${BULLET_PREFIX}${bullet}`,
  );
  return [decision, ...bullets].filter((line) => line.length > 0).join("\n");
}

/**
 * Textarea -> stored objective. Deterministic by design (stated in the design
 * spec): first non-empty line is the decision, every other non-empty line is a
 * bullet with ONE leading "- " stripped. The parse keeps every bullet the
 * marketer typed; the structural cap is enforced by validation (an error they
 * can act on), never by silently discarding their words.
 */
export function parseStudyObjective(text: string): StudyObjectiveDraft {
  const lines = (text ?? "")
    .split("\n")
    .map(sanitizeLine)
    .filter((line) => line.length > 0);
  const [decisionLine, ...bulletLines] = lines;
  const learn = bulletLines
    .map((line) =>
      line.startsWith(BULLET_PREFIX)
        ? line.slice(BULLET_PREFIX.length).trim()
        : line.replace(/^-(?=\S)/, "").trim(),
    )
    .filter((bullet) => bullet.length > 0);
  return { decision: (decisionLine ?? "").trim(), learn };
}

export type StudyObjectiveValidationCode =
  | "empty"
  | "too_many_bullets"
  | "too_long";

export interface StudyObjectiveValidation {
  valid: boolean;
  /** Ready-to-render message, or null when the text is fine. */
  error: string | null;
  code: StudyObjectiveValidationCode | null;
  /** Total characters counted against the single cap, after the silent fixes. */
  length: number;
}

/** The three messages the row can show. Kept here so the copy has one home. */
export const STUDY_OBJECTIVE_ERRORS: Record<
  StudyObjectiveValidationCode,
  string
> = {
  empty: "Add a decision line",
  too_many_bullets: `Keep it to ${STUDY_OBJECTIVE_MAX_BULLETS} bullets`,
  too_long: `Keep it under ${STUDY_OBJECTIVE_MAX_CHARS} characters`,
};

/**
 * Validate-then-store: the row runs this before every Save and disables Save
 * while it fails. Errors are the three a marketer can act on; everything else
 * is fixed silently by the sanitizer above.
 */
export function validateStudyObjectiveText(
  text: string,
): StudyObjectiveValidation {
  // The user's own text is stored as typed and shown as plain text
  // everywhere (owner 2026-10-22): markup is never refused or stripped here.
  const raw = text ?? "";
  const parsed = parseStudyObjective(raw);
  const length = studyObjectiveLength(parsed);
  if (!parsed.decision) {
    return {
      valid: false,
      error: STUDY_OBJECTIVE_ERRORS.empty,
      code: "empty",
      length,
    };
  }
  if (parsed.learn.length > STUDY_OBJECTIVE_MAX_BULLETS) {
    return {
      valid: false,
      error: STUDY_OBJECTIVE_ERRORS.too_many_bullets,
      code: "too_many_bullets",
      length,
    };
  }
  if (length > STUDY_OBJECTIVE_MAX_CHARS) {
    return {
      valid: false,
      error: STUDY_OBJECTIVE_ERRORS.too_long,
      code: "too_long",
      length,
    };
  }
  return { valid: true, error: null, code: null, length };
}

/** Decision + bullets share ONE budget, so the counter adds them up. */
export function studyObjectiveLength(objective: StudyObjectiveDraft): number {
  return (
    objective.decision.length +
    objective.learn.reduce((total, bullet) => total + bullet.length, 0)
  );
}

/** What the live counter shows while typing, from raw textarea text. */
export function countStudyObjectiveChars(text: string): number {
  return studyObjectiveLength(parseStudyObjective(text));
}
